import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Lenis from "lenis";
import { MotionConfig } from "framer-motion";
import { StoreProvider, useStore } from "./lib/store";
import Header from "./components/Header";
import Footer from "./components/Footer";
import CartDrawer from "./components/CartDrawer";
import SearchOverlay from "./components/SearchOverlay";
import { ScrollToTop, ConsentBanner, BackToTop } from "./components/Misc";
import { NewsletterPopup } from "./components/NewsletterPopup";
import Home from "./pages/Home";
import Collection from "./pages/Collection";
import Product from "./pages/Product";
import CartPage from "./pages/CartPage";
import Checkout from "./pages/Checkout";
import PaymentTest, { PaymentCancel } from "./pages/PaymentTest";
import PaymentSuccess from "./pages/PaymentResult";
import OrderConfirm from "./pages/OrderConfirm";
import TrackOrder from "./pages/TrackOrder";
import Account from "./pages/Account";
import Wishlist from "./pages/Wishlist";
import { Contact, FAQ, Policy, About } from "./pages/InfoPages";
import Admin from "./pages/Admin";
import NotFound from "./pages/NotFound";

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center p-8 text-center">
          <div>
            <h1 className="font-display text-2xl font-semibold">Something buzzed out of place</h1>
            <p className="text-charcoal/70 mt-2">Please refresh the page. If it persists, we'd love to hear about it.</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function useLenis() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ autoRaf: true, lerp: 0.11 });
    return () => lenis.destroy();
  }, []);
}

function SearchOverlayTied() {
  const { searchOpen, setSearchOpen } = useStore();
  return <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />;
}

function ShopChrome({ children }) {
  const { settings } = useStore();
  return (
    <>
      <Header />
      <main className="min-h-[60vh]">{children}</main>
      <Footer settings={settings} />
      <CartDrawer />
      <SearchOverlayTied />
      <ConsentBanner />
      <NewsletterPopup />
    </>
  );
}

export default function App() {
  useLenis();
  return (
    <ErrorBoundary>
      <StoreProvider>
        <MotionConfig reducedMotion="user">
          <BrowserRouter>
            <ScrollToTop />
            <BackToTop />
            <Routes>
              <Route path="/admin" element={<Admin />} />
              <Route path="/payment/test" element={<PaymentTest />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="*" element={
                <ShopChrome>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/collections/:slug" element={<Collection />} />
                    <Route path="/products/:slug" element={<Product />} />
                    <Route path="/cart" element={<CartPage />} />
                    <Route path="/checkout" element={<Checkout />} />
                    <Route path="/order-confirm/:orderNumber" element={<OrderConfirm />} />
                    <Route path="/track-order" element={<TrackOrder />} />
                    <Route path="/account" element={<Account />} />
                    <Route path="/wishlist" element={<Wishlist />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/faqs" element={<FAQ />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/policies/:slug" element={<Policy />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </ShopChrome>
              } />
            </Routes>
          </BrowserRouter>
        </MotionConfig>
      </StoreProvider>
    </ErrorBoundary>
  );
}
